import type {HeaderBiddingConfig} from "header-bidding-client-base/modules/config/domain/headerBiddingConfig";
import type {ILogger} from "ads-client-base/modules/logger/domain/iLogger";
import {HeaderBiddingPlatforms} from "header-bidding-base/modules/header-bidding/domain/platforms";
import type {IHeaderBiddingPlatform, VideoInstreamBidData} from "../../domain/headerBiddingPlatform";
import type {IAdUnitTypeDTO} from "services-comun-meteored/modules/services/header-bidding/interface";

declare global {
    interface Window {
        apstag?: any;
    }
}

export interface AmazonTamAdUnit<BidderAdUnitDef = unknown> {
    slotID: string,
    mediaType?: 'video',
    slotName: string,
    sizes?: number[][],
    slotParams?: any;
    floor?: {
        value: number,
        currency: string
    }
}

export class AmazonTamAdapter implements IHeaderBiddingPlatform<AmazonTamAdUnit> {
    private adUnits: AmazonTamAdUnit[];
    private bidTimeout: number;

    public constructor(
        private config: HeaderBiddingConfig,
        private logger: ILogger,
    ) {
        this.bidTimeout = config.platformsConfig[HeaderBiddingPlatforms.AMAZON_TAM]?.timeout || this.config.timeout;
        this.adUnits = [];
    }

    public get platform() {
        return HeaderBiddingPlatforms.AMAZON_TAM;
    }

    public async init() {
        this.logger.log("[Amazon TAM]", "Initializing...");

        this.loadAmazonTamScript();

        await this.setAmazonTamSettings();

        this.logger.log("[Amazon TAM]", "Initialized");
    }

    public addAdUnits(adUnits: { id: number, adUnitPath: string, divId: string, type: IAdUnitTypeDTO }[]) {
        const adUnitsParsed: AmazonTamAdUnit[] = adUnits.map((adUnit) => {
            const adUnitExists = this.getAdUnit(adUnit.divId);
            if (adUnitExists) {
                this.logger.warn("[Amazon TAM]", "Adunit already inserted", adUnit.divId);
                return;
            }

            if (adUnit.type.banner) {
                const amazonTAMAdUnit: AmazonTamAdUnit = {
                    slotID: adUnit.divId,
                    slotName: adUnit.adUnitPath,
                    sizes: adUnit.type.banner.sizes!,
                }

                this.logger.log("[Amazon TAM]", "Adunit " + adUnit.adUnitPath + " loaded", amazonTAMAdUnit);

                return amazonTAMAdUnit;
            }

            if (adUnit.type.video) {
                const amazonTAMAdUnit: AmazonTamAdUnit = {
                    slotID: adUnit.divId,
                    mediaType: 'video',
                    slotName: adUnit.adUnitPath,
                }

                this.logger.log("[Amazon TAM]", "Video adunit " + adUnit.adUnitPath + " loaded", amazonTAMAdUnit);

                return amazonTAMAdUnit;
            }

            this.logger.warn("[Amazon TAM]", "Adunit type not supported", adUnit);
            return;

        }).filter(adUnit => !!adUnit) as AmazonTamAdUnit[];

        this.adUnits = this.adUnits.concat(adUnitsParsed);

        this.logger.log("[Amazon TAM]", "Adunits added");

        return adUnitsParsed;

    }

    public getAdUnit(id: string) {
        return this.adUnits.find(adUnit => adUnit.slotID === id);
    }

    public removeAdUnits(adUnitIds: string[]): void {
        this.adUnits = this.adUnits.filter(adUnit => !adUnitIds.includes(adUnit.slotID));
        this.logger.log("[Amazon TAM]", "Adunits removed", adUnitIds);
    }

    public async requestBids(adUnitCodes: string[], disabledBidders?: { [bidder: string]: string[] }) {

        return new Promise<any>((resolve, reject) => {
            try {
                this.logger.log("[Amazon TAM]", "Requesting bids " + (adUnitCodes ? "for " + adUnitCodes.join(", ") : "") + "...");

                const adUnits = adUnitCodes && adUnitCodes.length > 0 ? this.adUnits.filter(adUnit => adUnitCodes.includes(adUnit.slotID)) : this.adUnits;

                if (adUnits.length === 0) {
                    return resolve({});
                }

                window.apstag.fetchBids({
                    slots: adUnits,
                    timeout: this.bidTimeout,
                }, (bids: any) => {

                    if (bids.length > 0) {
                        window.apstag.setDisplayBids();
                    }

                    const videoInstreamBidsData = this.getVideoInstreamBidsData(adUnits, bids);

                    this.logger.log("[Amazon TAM]", "Bids requested");
                    this.logger.log("[Amazon TAM]", bids);

                    return resolve({ videoInstreamBidsData });
                });
            } catch (error) {
                return reject(error);
            }
        });
    }

    private loadAmazonTamScript(): void {

        if (window["apstag"]) return;

        function q(c: any, r: any) {
            window["apstag"]._Q.push([c, r])
        }

        window["apstag"] = {
            init: function () {
                q("i", arguments)
            },
            fetchBids: function () {
                q("f", arguments)
            }, setDisplayBids: function () {
            }, targetingKeys: function () {
                return []
            }, _Q: []
        };
        const script = document.createElement("script");
        script.async = !0;
        script.src = "//c.amazon-adsystem.com/aax2/apstag.js";
        const g = document.getElementsByTagName("script")[0];
        g.parentNode?.insertBefore(script, g)
    }

    private async setAmazonTamSettings() {

        return new Promise<void>((resolve, reject) => {

            try {
                const options = {
                    pubID: this.config.platformsConfig[HeaderBiddingPlatforms.AMAZON_TAM]?.pubID,
                    adServer: 'googletag',
                    bidTimeout: this.bidTimeout,
                };

                window.apstag.init(options, () => {
                    return resolve();
                });

                this.logger.log("[Amazon TAM]", "Settings set", options);

            } catch (error) {
                return reject(error);
            }
        });
    }

    private getVideoInstreamBidsData(adUnits: AmazonTamAdUnit[], bids: any[]): VideoInstreamBidData[] {
        const videoAdUnits = adUnits.filter(adUnit => adUnit.mediaType === 'video');
        if (!videoAdUnits || !videoAdUnits.length) {
            return [];
        }

        return videoAdUnits.map(adUnit => {
            const adUnitCode = adUnit.slotID;
            const bid = bids.find((bid: any) => bid.slotID == adUnit.slotID);
            if (bid) {
                const qsParams = bid.qsParams;
                if (qsParams) {
                    const qsParamsSplit = qsParams.substring(1).split('&');
                    const adServerTargeting: any = {};

                    qsParamsSplit.forEach((param: any) => {
                        var [key, value] = param.split('=');
                        if (key && value) {
                            adServerTargeting[key] = value;
                        }
                    });

                    return {adUnitCode, state: 'bid', data: {bidderCode: 'tam', adServerTargeting}};
                }
            }

            return {
                adUnitCode: adUnitCode,
                state: 'nobid',
            };

        });
    }
}
